import React from 'react';
import moment from 'moment';
import classNames from 'classnames';
import { GridColumnProps } from '../../../../../components/shared/grid/types';
import { CostReportItem, getFileExtension } from '../../../../../features/cost-report/types';
import { Lx } from '../../../../../i18n/consts';
import DownloadButton from '../../../../../components/shared/file-download-button';
import CostAmountMenuSection from '../../cost-amount-menu-section';
import MobileCostRow from '../mobile-cost-row';
import styles from '../grid.module.scss';

export const DESKTOP_AMOUNT_COLUMN_WIDTH = 160;
export const DESKTOP_DATE_COLUMN_WIDTH = 110;
export const DESKTOP_PROJECT_COLUMN_WIDTH = 100;

interface CreateColumnsParams {
    onAddNewCostClick: (cost: CostReportItem) => void;
    onEditCostClick: (cost: CostReportItem) => void;
    onDeleteCostClick: (cost: CostReportItem) => void;
    canManageCost: (cost: CostReportItem) => boolean;
    isMobile: boolean;
}

export const createColumns = ({
    onAddNewCostClick,
    onEditCostClick,
    onDeleteCostClick,
    canManageCost,
    isMobile,
}: CreateColumnsParams): GridColumnProps[] => {
    if (isMobile) {
        return [
            {
                field: 'mobile',
                localizationKey: Lx.General.DATE,
                flex: { grow: 1 },
                isHeaderCollapseButton: true,
                cellRender: (field, row, intl) => (
                    <MobileCostRow
                        item={row as CostReportItem}
                        intl={intl}
                        onAddNewCostClick={canManageCost(row as CostReportItem) ? onAddNewCostClick : undefined}
                        onEditCostClick={canManageCost(row as CostReportItem) && (row as CostReportItem).id !== -1 ? onEditCostClick : undefined}
                        onDeleteCostClick={canManageCost(row as CostReportItem) && (row as CostReportItem).id !== -1 ? onDeleteCostClick : undefined}
                    />
                ),
            },
        ];
    }

    return [
        {
            field: 'date',
            localizationKey: Lx.General.DATE,
            editable: false,
            isHeaderCollapseButton: true,
            width: DESKTOP_DATE_COLUMN_WIDTH,
            cellRender: (field, row) => (
                <div className={classNames(styles.cell, styles.ellipsisCell)}>
                    {moment((row as CostReportItem).date).format('MM/DD/YYYY')}
                </div>
            ),
        },
        {
            field: 'project',
            localizationKey: Lx.General.PROJECT,
            editable: false,
            width: DESKTOP_PROJECT_COLUMN_WIDTH,
            value: (row) => (row as CostReportItem).projectId,
            cellRender: (field, row) => (
                <div className={classNames(styles.cell, styles.ellipsisCell)}>{(row as CostReportItem).projectName}</div>
            ),
            alignment: 'center',
            noHeader: true
        },
        {
            field: 'note',
            localizationKey: Lx.Cost.NOTE,
            flex: { grow: 3 },
            cellRender: (field, row) => (
                <div className={classNames(styles.cell, styles.noteCell, styles.scrollCell)}>
                    {(row as CostReportItem).note}
                </div>
            ),
        },
        {
            field: 'files',
            localizationKey: Lx.Cost.FILES,
            flex: { grow: 3 },
            cellRender: (field, row) => {
                const item = row as CostReportItem;
                return (
                    <div className={classNames(styles.cell, styles.scrollCell)}>
                        {item.files && item.files?.length > 0 ? (
                            <div className={styles.fileCells}>
                                {item.files.map((file) => {
                                    const extension = getFileExtension(file.extension);
                                    const fullFileName = `${file.name}${extension ? `.${extension}` : ''}`;
                                    return (
                                        <DownloadButton
                                            key={file.id}
                                            fileName={fullFileName}
                                            downloadUrl={file.fileDownloadUrl}
                                            downloadFileName={file.name}
                                        />
                                    );
                                })}
                            </div>
                        ) : null}
                    </div>
                );
            },
        },
        {
            field: 'amount',
            localizationKey: Lx.Cost.AMOUNT,
            width: DESKTOP_AMOUNT_COLUMN_WIDTH,
            cellRender: (field, row, intl) => {
                const item = row as CostReportItem;
                return (
                    <CostAmountMenuSection
                        intl={intl}
                        variant="item"
                        amount={item.amount}
                        onAddNewCostClick={canManageCost(item) ? () => onAddNewCostClick(item) : undefined}
                        onEditCostClick={canManageCost(item) && item.id !== -1 ? () => onEditCostClick(item) : undefined}
                        onDeleteCostClick={canManageCost(item) && item.id !== -1 ? () => onDeleteCostClick(item) : undefined}
                    />
                );
            },
        },
    ];
};

export const getAmountColumnWidth = (columns: GridColumnProps[]): number | undefined => {
    const amountColumn = columns.find((column) => column.field === 'amount');
    return amountColumn?.width;
};
